import { json, jsonParseLinter } from '@codemirror/lang-json'
import { linter } from '@codemirror/lint'
import {
  Alert,
  Button,
  CodeEditorV2,
  Dialog,
  Input,
  RadixTabs,
  Select,
} from '@portal/components'
import { isArray } from 'lodash'
import React, { useState } from 'react'

import { validateJsonObjectContent } from '../../utils'
import { useDQLEditorContext } from '../dqlEditor/context'
import { DataImportFile } from './DataImportFile'
import type { ImportMethod } from './types'

type DataImportDialogProps = {
  /**
   * True if the dialog is open.
   */
  isOpen: boolean
  /**
   * Callback to be called when the dialog is closed.
   */
  onClose: () => void
  /**
   * The method by which the data will be imported.
   */
  importMethod?: ImportMethod
}

export const DataImportDialog = ({
  isOpen,
  onClose,
  importMethod,
}: DataImportDialogProps) => {
  const { appCollections, isLoading, setQuery } = useDQLEditorContext()

  // Keeps new and existing collection selection separate
  const [tab, setTab] = useState<'existing' | 'new'>('existing')
  const [selectedCollection, setSelectedCollection] = useState<string>('')
  const [createNewCollection, setCreateNewCollection] = useState<string>('')

  // The import data settings and error state
  const [importData, setImportData] = useState<string>()
  const [uploadedFile, setUploadedFile] = useState<File | undefined>()
  const [formatError, setFormatError] = useState<string>('')

  const jsonLinter = linter(jsonParseLinter())

  const handleClose = () => {
    // Resets everything
    setSelectedCollection('')
    setCreateNewCollection('')
    setImportData(undefined)
    setUploadedFile(undefined)
    setFormatError('')
    onClose()
  }

  const handleCreateCollection = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCreateNewCollection(e.target.value)
    setSelectedCollection('')
  }

  const handleSelectCollection = (value: string) => {
    setSelectedCollection(value)
    setCreateNewCollection('')
  }

  const handleRunImportQuery = () => {
    if (isLoading || !importData) return

    // Normalize the data into a string, regardless of source
    const data = !uploadedFile ? importData : JSON.stringify(importData)

    if (!validateJsonObjectContent(data)) {
      setFormatError('Error importing data: Invalid format.')
      return
    }

    const collectionName = selectedCollection || createNewCollection

    // Insert single document or loop through if several
    if (!isArray(JSON.parse(data))) {
      // Uploaded files and JSON from the editor need to be handled differently
      setQuery(
        `INSERT INTO ${collectionName} DOCUMENTS (deserialize_json('${data}'))`,
      )
    } else {
      const jsonInserts = JSON.parse(data).map(
        (json: Record<string, unknown>) =>
          `(deserialize_json('${JSON.stringify(json)}'))`,
      )

      setQuery(
        `INSERT INTO ${collectionName} DOCUMENTS ${jsonInserts.join(', ')}`,
      )
    }

    handleClose()
  }

  const handleFileDropped = (file?: File, contents?: string) => {
    if (file && contents) {
      setUploadedFile(file)
      setImportData(contents)
    } else {
      setUploadedFile(undefined)
      setImportData(undefined)
    }
  }

  // Create a type-safe wrapper for setTab
  const handleTabChange = (value: string) => {
    if (value === 'existing' || value === 'new') {
      setTab(value)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <Dialog.Content className="space-y-5">
        <Dialog.Header>
          <Dialog.Title>{`Import data by ${importMethod || 'file'}`}</Dialog.Title>
        </Dialog.Header>

        {!!appCollections?.length && (
          <>
            <RadixTabs
              defaultValue="existing"
              value={tab}
              onValueChange={handleTabChange}
              className="w-full"
            >
              <RadixTabs.List className="w-full">
                <RadixTabs.Trigger value="existing" className="w-full">
                  Existing Collection
                </RadixTabs.Trigger>
                <RadixTabs.Trigger value="new" className="w-full">
                  New Collection
                </RadixTabs.Trigger>
              </RadixTabs.List>
            </RadixTabs>

            {tab === 'existing' && (
              <Select
                value={selectedCollection}
                label="Select a collection"
                onValueChange={handleSelectCollection}
                options={appCollections.map((c) => ({
                  label: c,
                  value: c,
                }))}
                placeholder="Select a collection"
                aria-label="Select a collection"
              />
            )}

            {tab === 'new' && (
              <Input
                label="Create a new collection"
                placeholder="Collection name"
                value={createNewCollection}
                onChange={handleCreateCollection}
                data-testid="create-collection-input"
              />
            )}
          </>
        )}

        {!appCollections?.length && (
          <Input
            label="Create a new collection"
            placeholder="Collection name"
            value={createNewCollection}
            onChange={handleCreateCollection}
            data-testid="create-collection-input"
          />
        )}

        {importMethod === 'file' && (
          <DataImportFile
            uploadedFile={uploadedFile}
            onDroppedFile={handleFileDropped}
            disabled={isLoading}
            className="h-[256px]"
          />
        )}

        {importMethod === 'json' && (
          <div
            className="w-full overflow-hidden"
            data-testid="importByJsonSection"
          >
            <div
              className="flex h-[240px] w-full flex-1 overflow-hidden rounded-lg border border-border-normal drop-shadow-sm"
              data-testid="codeEditor"
            >
              <CodeEditorV2
                height="240px"
                basicSetup={{
                  lineNumbers: true,
                  highlightActiveLineGutter: false,
                }}
                value={importData}
                language={json()}
                onChange={(value) => setImportData(value)}
                linter={jsonLinter}
                className="w-full"
              />
            </div>
          </div>
        )}

        {formatError && <Alert variant="danger">{formatError}</Alert>}

        <Dialog.Footer>
          <Button variant="ghost" onClick={handleClose}>
            Close
          </Button>
          <Button
            variant="primary"
            disabled={
              !!formatError ||
              (!selectedCollection && !createNewCollection) ||
              !importData
            }
            onClick={handleRunImportQuery}
          >
            Import
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default DataImportDialog
